import React, { FC, useState } from 'react';
import { PaginateListMore } from '../../../../../../common/types/common-types';
import { getDayEnd, getDayStart, useDataProvider } from 'src/app/common/utils';
import {
  fetchIncentiveRegistrationReportDownload,
  fetchIncentiveRegistrationReportList,
} from '../../../../network/reportCrud';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../../common/components/ParamsProvider';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import {
  ReportIncentiveRegistrationItem,
  ReportIncentiveRegistrationParams,
} from 'src/app/modules/Report/types/incentive-types';

type IncentiveResultListingPageProps = {} & ParamsProps;

const initialState: ReportIncentiveRegistrationParams = {
  registerDateFrom: getDayStart(new Date()),
  registerDateTo: getDayEnd(new Date()),
  agentCode: '',
  incentiveName: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): ReportIncentiveRegistrationParams => {
  return initialParams
    ? {
        registerDateFrom: initialParams.requestedDateFrom ? getDayStart(new Date(initialParams.requestedDateFrom)) : getDayStart(new Date()),
        registerDateTo: initialParams.requestedDateTo ? getDayEnd(new Date(initialParams.requestedDateTo)) : getDayEnd(new Date()),
        agentCode: '',
        incentiveName: '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const IncentiveResultListingPage: FC<IncentiveResultListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const defaultFilterValue = paramsInitiator(initialParams);
  const [filterState, setFilterState] = useState<ReportIncentiveRegistrationParams>(defaultFilterValue);

  const [reportList, setReportList] = useState<PaginateListMore<ReportIncentiveRegistrationItem>>();

  const { isLoading, refreshData } = useDataProvider<PaginateListMore<ReportIncentiveRegistrationItem>>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchIncentiveRegistrationReportList(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvFile = async () => {
    try {
      fetchIncentiveRegistrationReportDownload(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('incentiveRegistration.filter')}
        itemDef={[
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'registerDateFrom',
            fieldTo: 'registerDateTo',
            initialDateFrom: filterState.registerDateFrom,
            initialDateTo: filterState.registerDateTo,
            defaultDateFrom: defaultFilterValue.registerDateFrom,
            defaultDateTo: defaultFilterValue.registerDateTo,
            displayName: Translation('incentive.common.registerDate'),
            required: true,
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('incentive.common.agentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'incentiveName',
            initialValue: filterState.incentiveName,
            displayName: Translation('incentive.common.name'),
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            registerDateFrom: data.registerDateFrom,
            registerDateTo: data.registerDateTo,
            agentCode: data.agentCode,
            incentiveName: data.incentiveName,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('incentiveRegistration.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvFile(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'incentiveCode',
            displayName: Translation('incentive.common.code'),
            renderData: (row) => row.incentiveCode,
          },
          {
            keyIndex: 'incentiveName',
            displayName: Translation('incentive.common.name'),
            renderData: (row) => row.incentiveName,
          },
          {
            keyIndex: 'fromDate',
            displayName: Translation('incentive.common.durationFromDate'),
            renderData: (row) => row.fromDate,
          },
          {
            keyIndex: 'toDate',
            displayName: Translation('incentive.common.durationToDate'),
            renderData: (row) => row.toDate,
          },
          {
            keyIndex: 'agentCode',
            displayName: Translation('incentive.common.agentCode'),
            renderData: (row) => row.agentCode,
          },
          {
            keyIndex: 'agentName',
            displayName: Translation('incentive.common.agentFullName'),
            renderData: (row) => row.agentName,
          },
          {
            keyIndex: 'registerDate',
            displayName: Translation('incentive.common.registerDate'),
            renderData: (row) => row.registerDate,
          },
          {
            keyIndex: 'registerTime',
            displayName: Translation('incentive.common.registerTime'),
            renderData: (row) => row.registerTime,
          },
          {
            keyIndex: 'order',
            align: 'center',
            displayName: Translation('incentive.common.order'),
            renderData: (row) => String(row.order),
          },
          {
            keyIndex: 'option',
            align: 'center',
            displayName: Translation('incentive.common.option'),
            renderData: (row) => String(row.option),
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default IncentiveResultListingPage;
